import * as THREE from 'three';
import { CITIES } from '@/config/cities';
import { EnvironmentBuilder } from '@/render/Environment';
import { buildTrophy, type TrophyBuild } from '@/ui/title/trophy';
import { span } from '@/dev/perf';
import { yieldToMain } from '@/core/scheduler';
import type { EarthGlobe, TourRouteAnimation } from '@/world/Globe';

/**
 * TitleStage.ts — the flagship screen's 3D layer (M80): a transparent
 * full-screen canvas over the stadium backdrop.
 *
 *   - The WORLD TOUR CUP trophy stands front-left (procedural gold ribbons,
 *     ui/title/trophy.ts) with the SHARED Globe.ts earth seated in its cradle.
 *   - A second, big earth looms behind-right running the SAME leg-by-leg
 *     tour-route arc animation as the tour intro (createTourRouteAnimation).
 *   - FIRST show per page load: the globe BOUNCES its way into the cup —
 *     served in from the big globe's side, two floor bounces with soccer-ball
 *     squash, a final hop into the cradle, landing flash + shockwave ring —
 *     then the whole trophy settles into its slow idle rotation.
 *   - Later shows (returning from other screens): the globe is already seated;
 *     just the subtle idle (trophy rotation + route animation).
 *
 * Globe.ts stays a lazy chunk: the trophy renders immediately, the two earths
 * pop in when the module lands (cached after first load). Reduced motion
 * seats the globe instantly and freezes all idle motion.
 */

// ---- Camera / layout ----
const CAM_DIST = 16.5;
const CAM_HEIGHT = 3.55;
const CAM_FOV = 34;

// ---- Idle feel ----
const TROPHY_SPIN = 0.32; // rad/s — the subtle returning-home rotation
/** Background-planet opacity (owner: a subtle complement, the trophy is the
 *  hero). Solid surfaces set once; the additive arcs/pulses are capped per
 *  frame (the route animation re-writes their opacity every update). */
const BG_GLOBE_DIM = 0.4;
const BG_ARC_CAP = 0.3;

// ---- Bounce-in choreography (seconds) ----
const INTRO_DELAY = 0.55; // let the DOM stagger start first
const SEG_TIMES = [0.62, 0.5, 0.58] as const; // fall, bounce, hop-into-cup
const SQUASH_T = 0.1; // floor-contact squash length
const SETTLE_T = 0.5; // damped settle once seated
const BALL_SPIN = 4.2; // rad/s tumble while airborne

interface Anchor {
  trophyXFrac: number;
  trophyScale: number;
  globeXFrac: number;
  globeYFrac: number;
  globeDist: number;
  globeScale: number;
}

/** Layout per viewport shape: wide desktop / landscape phone / portrait. */
function anchorFor(aspect: number): Anchor {
  if (aspect <= 1.05) {
    // Portrait: the trophy IS the hero (ball in the gap under the brand);
    // the planet shrinks to a distant arc peeking over the very top.
    return {
      trophyXFrac: 0.5,
      trophyScale: 0.84,
      globeXFrac: 0.5,
      globeYFrac: 0.0,
      globeDist: 155,
      globeScale: 1.35,
    };
  }
  // Owner reference (2026-10-02): the trophy is CENTRE STAGE — between the
  // big left-column logo and the menu, larger — with the planet rising
  // behind it upper-right (it was tucked under the logo, front-left).
  if (aspect <= 1.6) {
    return {
      trophyXFrac: 0.45,
      trophyScale: 0.88,
      globeXFrac: 0.64,
      globeYFrac: 0.3,
      globeDist: 105,
      globeScale: 1.75,
    };
  }
  if (aspect <= 1.95) {
    return {
      trophyXFrac: 0.46,
      trophyScale: 0.9,
      globeXFrac: 0.66,
      globeYFrac: 0.33,
      globeDist: 100,
      globeScale: 1.9,
    };
  }
  // Landscape phones: a narrower stage between the logo and the menu column.
  return {
    trophyXFrac: 0.44,
    trophyScale: 0.86,
    globeXFrac: 0.64,
    globeYFrac: 0.36,
    globeDist: 100,
    globeScale: 1.85,
  };
}

/** Soft radial texture (contact shadow / flash / shockwave ring). */
function radialTexture(stops: [number, string][]): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 2, 64, 64, 62);
  for (const [o, col] of stops) grad.addColorStop(o, col);
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

function ringTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  g.strokeStyle = 'rgba(255, 214, 110, 1)';
  g.lineWidth = 7;
  g.shadowColor = 'rgba(255, 190, 70, 0.9)';
  g.shadowBlur = 10;
  g.beginPath();
  g.arc(64, 64, 46, 0, Math.PI * 2);
  g.stroke();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

/** One parabolic hop: start → end with the given apex CLEARANCE above the
 *  higher endpoint; s in [0,1]. */
function hop(
  start: THREE.Vector3,
  end: THREE.Vector3,
  clearance: number,
  s: number,
  out: THREE.Vector3,
): void {
  out.lerpVectors(start, end, s);
  const peak = Math.max(start.y, end.y) + clearance;
  // Quadratic through start.y (s=0), peak (s=0.5-ish), end.y (s=1).
  const base = start.y + (end.y - start.y) * s;
  out.y = base + (peak - Math.max(start.y, end.y)) * 4 * s * (1 - s);
}

export class TitleStage {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(CAM_FOV, 16 / 9, 0.1, 400);
  private env: EnvironmentBuilder | null = null;
  private raf: number | null = null;
  private lastT = 0;
  private elapsed = 0;

  private trophy: TrophyBuild | null = null;
  private trophyHolder = new THREE.Group(); // position/scale anchor
  private trophySpin = new THREE.Group(); // the idle rotation lives here
  private ballHolder = new THREE.Group(); // the seated/bouncing globe (inside trophySpin)
  private bigGlobeHolder = new THREE.Group();

  private smallEarth: EarthGlobe | null = null;
  private bigEarth: EarthGlobe | null = null;
  private route: TourRouteAnimation | null = null;
  private globesLoading = false;

  // Intro state machine: 'pending' waits for the globe chunk, then runs.
  private intro: 'none' | 'pending' | 'running' | 'done' = 'none';
  private introT = 0;
  private seated = false;
  private spinOn = false;

  private flash: THREE.Sprite | null = null;
  private shockRing: THREE.Sprite | null = null;
  private fxT = 1e9; // seconds since the landing flash fired

  private readonly mediaReduced =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** The game's own reduced-motion SETTING (M78 pattern: the setting gates JS
   *  animation, the media query gates CSS) — OR-ed with the OS preference. */
  private reducedPref = false;

  constructor(private readonly host: HTMLElement) {}

  setReducedMotion(on: boolean): void {
    this.reducedPref = on;
  }

  private get reducedMotion(): boolean {
    return this.mediaReduced || this.reducedPref;
  }

  private ensureRenderer(): void {
    if (this.renderer) return;
    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.domElement.className = 'title-stage-canvas';
    this.host.appendChild(r.domElement);
    this.renderer = r;

    // Warm stadium IBL — the gold NEEDS an environment to read as metal, and
    // the pitch-green globe picks up its sheen from the same map.
    this.env = new EnvironmentBuilder(r);
    this.scene.environment = this.env.generate({
      skyTop: 0x0a1020,
      skyHorizon: 0x3a2c14,
      sun: 0xffd9a0,
      sunIntensity: 3.2,
      sunDir: new THREE.Vector3(-0.4, 0.75, 0.55).normalize(),
      ground: 0x05060a,
    });

    // Stadium-night grade: warm key upper-left (the floodlights), cool blue
    // rim from the right, a hemisphere fill so nothing goes dead black, and a
    // warm floodlight point riding the trophy anchor so the gold always burns.
    this.scene.add(new THREE.HemisphereLight(0x92a8cc, 0x070a12, 0.85));
    const key = new THREE.DirectionalLight(0xffe6bc, 3.0);
    key.position.set(-7, 13, 12);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x7ea0ff, 1.1);
    rim.position.set(11, 6, -7);
    this.scene.add(rim);
    const flood = new THREE.PointLight(0xffd9a0, 140, 40, 2);
    flood.position.set(4.5, 7.5, 6.5);
    this.trophyHolder.add(flood);

    this.camera.position.set(0, CAM_HEIGHT, CAM_DIST);
    this.camera.lookAt(0, CAM_HEIGHT - 0.25, 0);

    // ---- The trophy (procedural — shows instantly, no chunk wait). ----
    this.trophy = span('title.trophy', () => buildTrophy());
    // Hero angle: start with the ramp's racecar front-right facing the camera
    // (the idle spin proceeds from here).
    this.trophySpin.rotation.y = -0.97;
    this.trophySpin.add(this.trophy.group);
    this.ballHolder.position.copy(this.trophy.cradle);
    this.ballHolder.visible = false; // until the globe chunk lands
    this.trophySpin.add(this.ballHolder);
    this.trophyHolder.add(this.trophySpin);
    this.scene.add(this.trophyHolder);

    // Grounding: soft contact shadow + a breathing gold podium ring, so the
    // trophy sits ON the backdrop floor (the garage/lobby trick).
    const shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(8.6, 8.6),
      new THREE.MeshBasicMaterial({
        map: radialTexture([
          [0, 'rgba(0,0,0,0.66)'],
          [0.6, 'rgba(0,0,0,0.3)'],
          [1, 'rgba(0,0,0,0)'],
        ]),
        transparent: true,
        depthWrite: false,
      }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.012;
    this.trophyHolder.add(shadow);
    const podium = new THREE.Mesh(
      new THREE.RingGeometry(2.9, 3.3, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffc95e,
        transparent: true,
        opacity: 0.32,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    podium.rotation.x = -Math.PI / 2;
    podium.position.y = 0.03;
    this.trophyHolder.add(podium);

    // ---- Landing FX (hidden until the bounce lands). ----
    // depthTest OFF on both: the flash sits at the ball's CENTRE and the ring
    // sweeps through the ribbons — depth-tested they'd be swallowed whole.
    this.flash = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: radialTexture([
          [0, 'rgba(255,240,200,1)'],
          [0.35, 'rgba(255,200,90,0.55)'],
          [1, 'rgba(255,180,60,0)'],
        ]),
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
      }),
    );
    this.flash.position.copy(this.trophy.cradle);
    this.flash.scale.setScalar(0.1);
    this.flash.renderOrder = 20;
    this.trophySpin.add(this.flash);
    // Camera-facing ring (a flat mesh read edge-on as a line — measured).
    this.shockRing = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: ringTexture(),
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
      }),
    );
    this.shockRing.position.copy(this.trophy.cradle);
    this.shockRing.renderOrder = 19;
    this.trophySpin.add(this.shockRing);

    this.scene.add(this.bigGlobeHolder);
  }

  /** Lazy-load Globe.ts (its own chunk) and mount both earths. */
  private loadGlobes(): void {
    if (this.globesLoading || this.bigEarth) return;
    this.globesLoading = true;
    void import('@/world/Globe')
      .then(async ({ buildEarth, createTourRouteAnimation, GLOBE_R }) => {
        if (!this.renderer) return; // disposed while loading
        // Loading round: the trophy's BALL first (it is what the bounce-in
        // needs, and it pays the shared soccer-panel bake), then a yield so
        // the title's first taps can land, THEN the big background planet.
        // Same two builds, same caches — just two tasks instead of one.
        this.smallEarth = span('title.earthBall', () => buildEarth(CITIES, 'ball'));
        const s = (this.trophy?.globeRadius ?? 1.5) / GLOBE_R;
        this.smallEarth.group.scale.setScalar(s);
        this.ballHolder.add(this.smallEarth.group);
        this.applyAnchors();
        if (this.intro === 'pending' && !this.reducedMotion) {
          this.intro = 'running';
          this.introT = -INTRO_DELAY;
          // Stays hidden through the delay beat — updateIntro reveals it at
          // its off-screen start point (never a frame parked in the cradle).
        } else {
          this.seatBall();
        }
        await yieldToMain();
        if (!this.renderer) return;
        // The big background planet, rolling its tour route forever — DIMMED
        // to a background complement (owner note: oversized but subtle).
        this.bigEarth = span('title.earthBig', () => buildEarth(CITIES));
        this.route = createTourRouteAnimation(this.bigEarth, CITIES);
        this.bigEarth.group.traverse((o) => {
          const m = (o as THREE.Mesh).material as
            | (THREE.Material & { opacity: number; emissiveIntensity?: number })
            | undefined;
          if (!m || Array.isArray(m)) return;
          if (m.blending !== THREE.AdditiveBlending) {
            m.transparent = true;
            m.opacity = Math.min(m.opacity, BG_GLOBE_DIM);
            if (m.emissiveIntensity !== undefined) m.emissiveIntensity *= 0.6;
          }
        });
        this.bigGlobeHolder.add(this.bigEarth.group);
        this.applyAnchors();
      })
      .catch((e) => {
        // A vite-dev recompile mid-import can fail silently (the M65 lesson) —
        // the trophy still shows; log so a missing planet is diagnosable.
        console.warn('[TitleStage] globe chunk failed to load', e);
      });
  }

  /** Reset + (re)arm the bounce-in. The ball's flight is a pure function of
   *  introT, so re-running is just a state reset; the landing-FX sprites are
   *  zeroed explicitly (updateFx early-outs on a huge fxT, so a restart
   *  mid-flash would otherwise freeze them bright). */
  private beginIntro(): void {
    this.seated = false;
    this.spinOn = false;
    this.fxT = 1e9;
    for (const sp of [this.flash, this.shockRing]) {
      if (sp) (sp.material as THREE.SpriteMaterial).opacity = 0;
    }
    this.ballHolder.visible = false;
    this.ballHolder.scale.setScalar(1);
    if (this.smallEarth) {
      this.intro = 'running';
      this.introT = -INTRO_DELAY;
    } else {
      this.intro = 'pending'; // loadGlobes() flips it to running on arrival
    }
  }

  /** Put the globe in the cradle and start the idle spin (no intro). */
  private seatBall(): void {
    this.intro = 'done';
    this.seated = true;
    this.spinOn = true;
    this.ballHolder.visible = true;
    this.ballHolder.position.copy(this.trophy!.cradle);
    this.ballHolder.scale.setScalar(1);
  }

  /** `intro` = play the bounce-in. M102c (owner rule): the full landing
   *  plays on EVERY arrival at the title now — show() restarts it. Account
   *  repaints while the title is up never call show() anymore
   *  (TitleScreen.refreshAccount swaps the corner in place): a mid-flight
   *  re-show used to hit the old "not seated → seat it" branch and SNAP the
   *  ball into the cup — the owner's missing-bounce + blink report. */
  show(opts: { intro: boolean }): void {
    this.ensureRenderer();
    if (this.renderer) this.renderer.domElement.style.display = '';
    if (opts.intro && !this.reducedMotion) this.beginIntro();
    else if (!this.seated && this.smallEarth) this.seatBall();
    else if (!this.smallEarth) this.intro = 'none'; // the loader seats it
    this.loadGlobes();
    this.resize();
    if (this.raf === null) {
      this.lastT = performance.now();
      const tick = (): void => {
        this.raf = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.05, (now - this.lastT) / 1000);
        this.lastT = now;
        this.update(dt);
        this.renderer?.render(this.scene, this.camera);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  private update(dt: number): void {
    this.elapsed += dt;
    if (!this.reducedMotion) {
      this.route?.update(dt); // big planet rolls its tour route
      // Cap the freshly-written arc/pulse opacities to the background dim.
      this.bigEarth?.group.traverse((o) => {
        const m = (o as THREE.Mesh).material as (THREE.Material & { opacity: number }) | undefined;
        if (m && !Array.isArray(m) && m.blending === THREE.AdditiveBlending) {
          m.opacity = Math.min(m.opacity, BG_ARC_CAP);
        }
      });
      this.smallEarth?.update(this.elapsed); // seated planet's markers breathe
      if (this.spinOn) this.trophySpin.rotation.y += dt * TROPHY_SPIN;
    }
    if (this.intro === 'running') this.updateIntro(dt);
    this.updateFx(dt);
  }

  /** The bounce-in: served in from the big globe's side, two floor bounces
   *  with squash, a final hop into the cradle, then the landing moment. */
  private updateIntro(dt: number): void {
    this.introT += dt;
    const t = this.introT;
    if (t < 0) return; // the INTRO_DELAY beat — DOM staggers in first
    this.ballHolder.visible = true;
    const cradle = this.trophy!.cradle;
    const ballR = this.trophy!.globeRadius;
    // Waypoints in trophy-local space (the spin is off until it lands).
    const p0 = new THREE.Vector3(10.5, cradle.y + 7.5, 1.2); // off-screen, globe side
    const b1 = new THREE.Vector3(5.2, ballR, 1.0); // first floor contact
    const b2 = new THREE.Vector3(2.9, ballR, 0.5); // second, shorter
    const pos = new THREE.Vector3();

    const seg = (idx: number): number =>
      (t - SEG_TIMES.slice(0, idx).reduce((a, b) => a + b, 0) - idx * SQUASH_T) / SEG_TIMES[idx];

    const squash = (contactT: number): number => {
      // 1 → squashed → 1 over SQUASH_T around a floor contact.
      const s = (t - contactT) / SQUASH_T;
      return s > 0 && s < 1 ? 1 - 0.2 * Math.sin(Math.PI * s) : 1;
    };

    const end1 = SEG_TIMES[0];
    const end2 = end1 + SQUASH_T + SEG_TIMES[1];
    const end3 = end2 + SQUASH_T + SEG_TIMES[2];

    if (t < end1) {
      // Falling entry — ease-in vertical drop along a shallow arc.
      const s = seg(0);
      pos.lerpVectors(p0, b1, s);
      pos.y = p0.y + (b1.y - p0.y) * s * s; // gravity: accelerating fall
    } else if (t < end1 + SQUASH_T) {
      pos.copy(b1);
    } else if (t < end2) {
      hop(b1, b2, 2.4, seg(1), pos);
    } else if (t < end2 + SQUASH_T) {
      pos.copy(b2);
    } else if (t < end3) {
      hop(b2, cradle, 1.7, seg(2), pos);
    } else if (t < end3 + SETTLE_T) {
      // Seated — a damped settle wobble sells the catch.
      pos.copy(cradle);
      const s = (t - end3) / SETTLE_T;
      const wob = Math.exp(-4.2 * s) * Math.sin(s * 18);
      this.ballHolder.scale.set(1 - wob * 0.06, 1 + wob * 0.1, 1 - wob * 0.06);
      if (this.fxT > 1e8) this.landingMoment(); // fire once, on first settle frame
    } else {
      this.intro = 'done';
      this.seated = true;
      this.spinOn = true;
      this.ballHolder.scale.setScalar(1);
      this.ballHolder.position.copy(cradle);
      return;
    }
    // Soccer-ball squash on the floor contacts (centre dips as it compresses)
    // + a rolling tumble while airborne.
    if (t < end3) {
      const sq = Math.min(squash(end1), squash(end2));
      if (sq < 1) pos.y = Math.min(pos.y, ballR * sq);
      this.ballHolder.scale.set(1 + (1 - sq) * 0.65, sq, 1 + (1 - sq) * 0.65);
      if (this.smallEarth) this.smallEarth.group.rotation.z -= dt * BALL_SPIN;
    }
    this.ballHolder.position.copy(pos);
  }

  /** Landing flash + expanding gold shockwave ring at the cradle. */
  private landingMoment(): void {
    this.fxT = 0;
  }

  private updateFx(dt: number): void {
    if (this.fxT > 3) return;
    this.fxT += dt;
    const s = this.fxT;
    if (this.flash) {
      const m = this.flash.material as THREE.SpriteMaterial;
      m.opacity = Math.max(0, 1 - s / 0.45);
      this.flash.scale.setScalar(0.5 + s * 9);
    }
    if (this.shockRing) {
      const m = this.shockRing.material as THREE.SpriteMaterial;
      m.opacity = Math.max(0, 0.9 - s / 0.7);
      const k = 0.6 + s * 10;
      this.shockRing.scale.set(k, k, k);
    }
  }

  /** Screen-anchored placement (the M64 trick, simplified): the trophy's axis
   *  and the big globe's centre land at viewport FRACTIONS whatever the
   *  aspect, so desktop / landscape phone / portrait each compose right. */
  private applyAnchors(): void {
    const a = anchorFor(this.camera.aspect);
    const vHalf = (CAM_FOV * Math.PI) / 360;
    const halfW = Math.tan(vHalf) * this.camera.aspect * CAM_DIST;
    this.trophyHolder.position.x = (a.trophyXFrac * 2 - 1) * halfW;
    this.trophyHolder.scale.setScalar(a.trophyScale);
    // The big planet: unproject its viewport point to a far depth.
    this.camera.updateMatrixWorld();
    const ndc = new THREE.Vector3(a.globeXFrac * 2 - 1, -(a.globeYFrac * 2 - 1), 0.5).unproject(
      this.camera,
    );
    const dir = ndc.sub(this.camera.position).normalize();
    this.bigGlobeHolder.position.copy(this.camera.position).addScaledVector(dir, a.globeDist);
    this.bigGlobeHolder.scale.setScalar(a.globeScale);
  }

  hide(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    if (this.renderer) this.renderer.domElement.style.display = 'none';
  }

  resize(): void {
    if (!this.renderer) return;
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.applyAnchors();
  }

  dispose(): void {
    this.hide();
    this.trophy?.dispose();
    this.env?.dispose();
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) for (const m of mat) m.dispose();
      else if (mat) mat.dispose();
    });
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
    this.renderer = null;
  }
}
